import toast from "react-hot-toast";

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, string[]>;
}

interface AxiosLikeError extends Error {
  response?: {
    data?: {
      error?: ApiError;
      message?: string;
    };
    status?: number;
  };
  code?: string;
}

/**
 * Extracts a user-friendly error message from various error types
 */
export function extractErrorMessage(error: unknown): string {
  if (!error) {
    return "Error desconocido";
  }

  // Handle Axios-like errors
  if (error instanceof Error) {
    const axiosError = error as AxiosLikeError;

    // Check for API error response
    if (axiosError.response?.data?.error?.message) {
      return axiosError.response.data.error.message;
    }

    // Check for simple message in response
    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }

    // Check for network errors
    if (axiosError.code === "ERR_NETWORK") {
      return "Error de conexión. Verifica tu conexión a internet.";
    }

    if (axiosError.code === "ECONNABORTED") {
      return "La solicitud tardó demasiado. Intenta de nuevo.";
    }

    // Check for HTTP status codes
    if (axiosError.response?.status) {
      switch (axiosError.response.status) {
        case 401:
          return "Sesión expirada. Por favor inicia sesión nuevamente.";
        case 403:
          return "No tienes permiso para realizar esta acción.";
        case 404:
          return "El recurso solicitado no fue encontrado.";
        case 422:
          return "Los datos enviados no son válidos.";
        case 429:
          return "Demasiadas solicitudes. Espera un momento e intenta de nuevo.";
        case 500:
          return "Error del servidor. Intenta de nuevo más tarde.";
        case 502:
        case 503:
        case 504:
          return "El servidor no está disponible. Intenta de nuevo más tarde.";
      }
    }

    // Return the error message if available
    if (axiosError.message) {
      return axiosError.message;
    }
  }

  // Handle string errors
  if (typeof error === "string") {
    return error;
  }

  return "Error desconocido";
}

/**
 * Handles API errors by showing a toast notification
 */
export function handleApiError(error: unknown, fallbackMessage?: string): void {
  const message = extractErrorMessage(error);
  toast.error(message || fallbackMessage || "Ha ocurrido un error");
}

/**
 * Extracts validation errors from an API response
 */
export function extractValidationErrors(
  error: unknown,
): Record<string, string[]> | null {
  if (error instanceof Error) {
    const axiosError = error as AxiosLikeError;
    const details = axiosError.response?.data?.error?.details;
    if (details && typeof details === "object") {
      return details;
    }
  }
  return null;
}

/**
 * Formats validation errors into a single string
 */
export function formatValidationErrors(
  errors: Record<string, string[]>,
): string {
  const messages: string[] = [];
  for (const [field, fieldErrors] of Object.entries(errors)) {
    if (Array.isArray(fieldErrors)) {
      messages.push(`${field}: ${fieldErrors.join(", ")}`);
    }
  }
  return messages.join("\n");
}
